import api from "./apiClient";
import { setCompanyContext } from "./apiClient";

/* =====================================================
   VALIDATION HELPERS
===================================================== */

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value);

const validateCountry = (country) => {
  if (country === undefined) {
    return undefined;
  }

  if (typeof country !== "string") {
    throw new Error("Country must be a string");
  }

  const normalized = country.trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(normalized)) {
    throw new Error(
      "Country must be a two-letter ISO country code, such as GU or IN"
    );
  }

  return normalized;
};

const normalizeCreateCompanyPayload = (payload) => {
  if (!isPlainObject(payload)) {
    throw new Error("A valid company payload is required");
  }

  if (
    typeof payload.name !== "string" ||
    !payload.name.trim()
  ) {
    throw new Error("Company name is required");
  }

  if (payload.name.trim().length > 200) {
    throw new Error("Company name cannot exceed 200 characters");
  }

  const normalized = {
    ...payload,
    name: payload.name.trim(),
  };

  if (payload.address !== undefined) {
    if (!isPlainObject(payload.address)) {
      throw new Error("Address must be a valid object");
    }

    normalized.address = { ...payload.address };

    if (payload.address.country !== undefined) {
      normalized.address.country = validateCountry(
        payload.address.country
      );
    }
  }

  return normalized;
};

const normalizeBillingPayload = (payload) => {
  if (!isPlainObject(payload)) {
    throw new Error("A valid billing payload is required");
  }

  const normalized = { ...payload };

  if (payload.address !== undefined) {
    if (!isPlainObject(payload.address)) {
      throw new Error("Address must be a valid object");
    }

    normalized.address = { ...payload.address };

    if (payload.address.country !== undefined) {
      normalized.address.country = validateCountry(
        payload.address.country
      );
    }
  }

  return normalized;
};

/* =====================================================
   GET MY COMPANIES
   GET /company
===================================================== */

export const getMyCompanies = async () => {
  try {
    const { data } = await api.get("/company");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch companies");
  }
};

/* =====================================================
   CREATE COMPANY
   POST /company/create
===================================================== */

export const createCompany = async (payload) => {
  const normalizedPayload = normalizeCreateCompanyPayload(payload);

  try {
    const { data } = await api.post(
      "/company/create",
      normalizedPayload
    );

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to create company");
  }
};

/* =====================================================
   GET COMPANY DETAILS
   GET /company/:id
===================================================== */

export const getCompanyDetails = async (companyId) => {
  if (!companyId) {
    throw new Error("Company ID is required");
  }

  try {
    const { data } = await api.get(`/company/${companyId}`);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to fetch company details");
  }
};

/* =====================================================
   GET COMPANY USERS
   GET /company/users
===================================================== */

export const getCompanyUsers = async () => {
  try {
    const { data } = await api.get("/company/users");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch company users");
  }
};

/* =====================================================
   INVITE MEMBER
   POST /company/invite
===================================================== */

export const inviteCompanyMember = async (payload) => {
  if (!payload?.email) {
    throw new Error("Email is required");
  }

  try {
    const { data } = await api.post("/company/invite", payload);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to invite member");
  }
};

/* =====================================================
   UPDATE BILLING
   PATCH /company/:id/billing
===================================================== */

export const updateCompanyBilling = async (companyId, payload) => {
  if (!companyId) {
    throw new Error("Company ID is required");
  }

  const normalizedPayload = normalizeBillingPayload(payload);

  try {
    const { data } = await api.patch(
      `/company/${companyId}/billing`,
      normalizedPayload
    );

    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to update billing");
  }
};

/* =====================================================
   DELETE COMPANY
   DELETE /company/:id
===================================================== */

export const deleteCompany = async (companyId) => {
  if (!companyId) {
    throw new Error("Company ID is required");
  }

  try {
    const { data } = await api.delete(`/company/${companyId}`);
    return data;
  } catch (err) {
    throw normalizeError(err, "Failed to delete company");
  }
};

/* =====================================================
   SET ACTIVE COMPANY
===================================================== */

export const selectCompany = (companyId) => {
  if (!companyId) {
    throw new Error("Company ID is required");
  }

  setCompanyContext(companyId);
};

/* =====================================================
   GET CONTRACTORS
   GET /company/contractors
===================================================== */

export const getContractors = async () => {
  try {
    const { data } = await api.get("/company/contractors");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw normalizeError(err, "Failed to fetch contractors");
  }
};

/* =====================================================
   ERROR NORMALIZER
===================================================== */

function normalizeError(err, fallbackMessage) {
  const message =
    err?.response?.data?.message ||
    err?.message ||
    fallbackMessage;

  const normalizedError = new Error(message);

  normalizedError.status = err?.response?.status;
  normalizedError.code = err?.code;
  normalizedError.details = err?.response?.data?.errors;

  return normalizedError;
}

/* =====================================================
   EXPORT
===================================================== */

const companyService = {
  getMyCompanies,
  createCompany,
  getCompanyDetails,
  getCompanyUsers,
  inviteCompanyMember,
  updateCompanyBilling,
  deleteCompany,
  selectCompany,
  getContractors,
};

export default companyService;